import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Mail, MoreHorizontal, Search, ShieldCheck, UserPlus, Eye } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TablePagination,
  TableRow,
  TableScroller,
  TableSkeletonRows,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatLocalDate } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import { messages } from '@/i18n';
import { useApi } from '@/api/apiContext';
import { ApiError } from '@/api/errors';
import {
  reactivateUserMutationOptions,
  resendInviteMutationOptions,
  suspendUserMutationOptions,
  usersQueryOptions,
} from '@/api/endpoints/users';
import { DASHBOARD_ROLES, type DashboardRole } from '@/types/session';
import { DASHBOARD_USER_STATUSES, type DashboardUserStatus } from '@/types/dashboardUser';
import {
  initialsFor,
  patchCachedUserRow,
  STATUS_BADGE_CLASS,
  toDisplayUser,
  USERS_PAGE_SIZE,
} from './userDirectoryData';

const PAGE_SIZE = USERS_PAGE_SIZE;

export function UsersScreen() {
  const t = messages.access;
  const api = useApi();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<DashboardRole | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<DashboardUserStatus | 'all'>('all');
  const [skip, setSkip] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; failed: boolean } | null>(null);
  const noticeTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  function announce(text: string, failed: boolean) {
    setNotice({ text, failed });
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 2500);
  }

  function messageFor(error: unknown, fallback: string): string {
    return error instanceof ApiError ? (error.problem?.detail ?? error.message) : fallback;
  }

  const suspendMutation = useMutation({
    ...suspendUserMutationOptions(api),
    onSuccess: (updated) => {
      setActionError(null);
      patchCachedUserRow(queryClient, updated.id, () => updated);
    },
    onError: (error) => setActionError(messageFor(error, t.suspendError)),
  });

  const reactivateMutation = useMutation({
    ...reactivateUserMutationOptions(api),
    onSuccess: (updated) => {
      setActionError(null);
      patchCachedUserRow(queryClient, updated.id, () => updated);
    },
    onError: (error) => setActionError(messageFor(error, t.reactivateError)),
  });

  // Resend changes no row state (Invited has no transition to itself), so the
  // only thing to report is that the email went out — announced in the same
  // live region the copy-email confirmation uses.
  const resendInviteMutation = useMutation({
    ...resendInviteMutationOptions(api),
    onSuccess: () => {
      setActionError(null);
      announce(t.resendInviteSuccess, false);
    },
    onError: (error) => setActionError(messageFor(error, t.resendInviteError)),
  });

  const params = useMemo(
    () => ({
      skip,
      take: PAGE_SIZE,
      search: query.trim() || undefined,
      userType: roleFilter === 'all' ? undefined : [roleFilter],
      status: statusFilter === 'all' ? undefined : [statusFilter],
    }),
    [skip, query, roleFilter, statusFilter],
  );

  const usersQuery = useQuery({
    ...usersQueryOptions(api, params),
    placeholderData: keepPreviousData,
  });

  const users = useMemo(() => (usersQuery.data?.items ?? []).map(toDisplayUser), [usersQuery.data]);

  const total = usersQuery.data?.total ?? 0;
  const rangeStart = total === 0 ? 0 : skip + 1;
  const rangeEnd = Math.min(skip + PAGE_SIZE, total);

  // An action error names a specific row, so it stops being meaningful the
  // moment the visible set of rows changes — clear it on every filter change
  // and page move. (Leaving the screen unmounts the component, which resets it.)
  function resetToFirstPage() {
    setSkip(0);
    setActionError(null);
  }

  function goToPage(nextSkip: number) {
    setSkip(nextSkip);
    setActionError(null);
  }

  function copyEmail(email: string) {
    // navigator.clipboard is undefined outside a secure context, and writeText
    // still rejects when the permission is denied — both need to say so.
    const written = navigator.clipboard?.writeText(email);
    if (written === undefined) {
      announce(t.copyEmailError, true);
      return;
    }
    void written.then(
      () => announce(t.copyEmailSuccess, false),
      () => announce(t.copyEmailError, true),
    );
  }

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{messages.nav.access}</h1>
          <p className="text-muted-foreground mt-1 max-w-prose text-sm">{t.subtitle}</p>
        </div>
        <Button asChild>
          <Link to="invite">
            <UserPlus />
            {t.inviteUser}
          </Link>
        </Button>
      </div>

      <Card className="mb-4 sm:max-w-3xs">
        <CardContent className="p-4">
          <p className="text-muted-foreground text-xs font-medium">Total users</p>
          <p className="font-headline text-2xl font-semibold">{total}</p>
        </CardContent>
      </Card>

      <Card>
        <div className="border-border/60 flex flex-wrap items-center gap-2 border-b p-4">
          <div className="border-border bg-secondary/40 flex min-w-[220px] flex-1 items-center gap-2 rounded-md border px-3 py-2 sm:max-w-xs">
            <Search className="text-muted-foreground size-4 shrink-0" />
            <input
              className="text-foreground placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none"
              placeholder={t.searchPlaceholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                resetToFirstPage();
              }}
            />
          </div>
          <select
            className="border-border bg-secondary/40 h-9 rounded-md border px-3 text-sm outline-none"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as DashboardRole | 'all');
              resetToFirstPage();
            }}
          >
            <option value="all">{t.roleFilterAll}</option>
            {DASHBOARD_ROLES.map((role) => (
              <option key={role} value={role}>
                {messages.roles[role]}
              </option>
            ))}
          </select>
          <select
            className="border-border bg-secondary/40 h-9 rounded-md border px-3 text-sm outline-none"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as DashboardUserStatus | 'all');
              resetToFirstPage();
            }}
          >
            <option value="all">{t.statusFilterAll}</option>
            {DASHBOARD_USER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          {/* Always rendered, so the live region exists before it has anything
              to announce — mounting it alongside the text can be missed. */}
          <span
            role="status"
            aria-live="polite"
            className={cn(
              'ml-auto text-sm',
              notice?.failed ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {notice?.text ?? ''}
          </span>
        </div>

        {actionError ? (
          <Alert variant="destructive" className="m-4">
            <Info aria-hidden />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        ) : null}

        {usersQuery.isError ? (
          <Alert variant="destructive" className="m-4">
            <Info aria-hidden />
            <AlertDescription>{t.loadError}</AlertDescription>
          </Alert>
        ) : (
          <TableScroller label={messages.nav.access} boundHeight>
            <Table columnCount={6} className="min-w-[720px]">
              <TableHeader sticky>
                <tr>
                  <TableHead>{t.columnUser}</TableHead>
                  <TableHead>{t.columnRole}</TableHead>
                  <TableHead>{t.columnOrganisation}</TableHead>
                  <TableHead>{t.columnStatus}</TableHead>
                  <TableHead>{t.columnDateAdded}</TableHead>
                  <TableHead>
                    <span className="sr-only">{t.columnActions}</span>
                  </TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {usersQuery.isLoading ? (
                  <TableSkeletonRows rows={8} />
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Link to={user.id} className="flex items-center gap-3">
                          <span className="bg-primary/20 text-primary-foreground font-headline flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                            {initialsFor(user.name)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-medium hover:underline">{user.name}</p>
                            <p className="text-muted-foreground truncate text-xs">{user.email}</p>
                          </div>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant="accent">{messages.roles[user.role]}</Badge>
                      </TableCell>
                      <TableCell muted>{user.organisationName ?? '—'}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_BADGE_CLASS[user.status]}>
                          {user.status === 'Active'
                            ? t.statusActive
                            : user.status === 'Invited'
                              ? t.statusInvited
                              : t.statusDeactivated}
                        </Badge>
                        {user.status === 'Invited' ? (
                          <p className="text-muted-foreground mt-1 text-xs">
                            {t.statusInvitedHint}
                          </p>
                        ) : null}
                        {user.status === 'Deactivated' ? (
                          <p className="text-muted-foreground mt-1 text-xs">
                            {t.statusDeactivatedHint}
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell muted className="whitespace-nowrap tabular-nums">
                        {formatLocalDate(user.dateAdded)}
                      </TableCell>
                      <TableCell align="right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Row actions">
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={user.id}>
                                <Eye aria-hidden />
                                {t.viewDetails}
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {user.status === 'Invited' ? (
                              <DropdownMenuItem
                                disabled={resendInviteMutation.isPending}
                                onSelect={() => resendInviteMutation.mutate(user.id)}
                              >
                                <Mail aria-hidden />
                                {t.resendInvite}
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuItem onSelect={() => copyEmail(user.email)}>
                              <Mail aria-hidden />
                              {t.copyEmail}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {user.status === 'Deactivated' ? (
                              <DropdownMenuItem
                                disabled={reactivateMutation.isPending}
                                onSelect={() => reactivateMutation.mutate(user.id)}
                              >
                                <ShieldCheck aria-hidden />
                                {t.reactivateAccess}
                              </DropdownMenuItem>
                            ) : user.status === 'Active' ? (
                              <DropdownMenuItem
                                disabled={suspendMutation.isPending}
                                onSelect={() => suspendMutation.mutate(user.id)}
                              >
                                <ShieldCheck aria-hidden />
                                {t.deactivateAccess}
                              </DropdownMenuItem>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {!usersQuery.isLoading && users.length === 0 ? (
                  <TableMessageRow>{t.emptyState}</TableMessageRow>
                ) : null}
              </TableBody>
            </Table>
          </TableScroller>
        )}

        <TablePagination
          summary={
            usersQuery.isLoading
              ? t.loading
              : `${total === 0 ? '0' : `${rangeStart}–${rangeEnd}`} / ${total}`
          }
          previousLabel={t.previousPage}
          nextLabel={t.nextPage}
          canGoBack={skip > 0}
          canGoForward={rangeEnd < total}
          onPrevious={() => goToPage(Math.max(0, skip - PAGE_SIZE))}
          onNext={() => goToPage(skip + PAGE_SIZE)}
        />
      </Card>
    </section>
  );
}
